import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navigation from '../../components/Navigation';
import ImagesTable from '../../components/ClipAudioWorkflow/UtilsComponents/ImagesTable';
import ImagesAnglesDisplay from '../../components/ClipAudioWorkflow/UtilsComponents/ImagesAnglesDisplay';
import { clipAudioApi, aiModelsApi } from '../../services/api';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Grid,
  Chip,
  Paper,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { 
  ArrowBack,
  MusicNote, 
  Movie, 
  Image, 
  Animation,
  CheckCircle,
  Schedule,
  ExpandMore,
  ExpandLess,
  Delete as DeleteIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Collapse state for each step - only show current step by default
  const [collapsedSteps, setCollapsedSteps] = useState({
    step1: true,
    step2: true,
    step3: true,
    step4: true
  });


  // Step generation/validation state
  const [stepStates, setStepStates] = useState({
    step1: { isGenerating: false, isValidating: false },
    step2: { isGenerating: false, isValidating: false },
    step3: { isGenerating: false, isValidating: false },
    step4: { isGenerating: false, isValidating: false }
  });

  // AI Models state
  const [imageGenerationModels, setImageGenerationModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [modelsLoading, setModelsLoading] = useState(false);
  
  // Number of images per scene state
  const [nbImagesPerScene, setNbImagesPerScene] = useState(3);

  // Scene editing state (Step 2)
  const [editingSceneIndex, setEditingSceneIndex] = useState(null);
  const [editingSceneText, setEditingSceneText] = useState('');
  const [isSavingScene, setIsSavingScene] = useState(false);
  const canEditScenes = project?.current_step === 2;

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const data = await clipAudioApi.getProject(id);
        setProject(data);
        
        // Auto-expand the current step
        const currentStep = data.current_step;
        setCollapsedSteps({
          step1: currentStep !== 1,
          step2: currentStep !== 2,
          step3: currentStep !== 3,
          step4: currentStep !== 4
        });
      } catch (err) {
        console.error('Error fetching project:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProject();
    }
  }, [id]);

  // Fetch AI models for image generation
  useEffect(() => {
    const fetchModels = async () => {
      try {
        setModelsLoading(true);
        const models = await aiModelsApi.getAIModels('image_generation_models');
        
        // Ensure models is always an array
        const modelsArray = Array.isArray(models) ? models : [];
        setImageGenerationModels(modelsArray);
        
        // Set the first model as default if available
        if (modelsArray.length > 0) {
          setSelectedModel(modelsArray[0].id);
        }
      } catch (err) {
        console.error('Error fetching AI models:', err);
        setImageGenerationModels([]); // Set empty array on error
        setError('Erreur lors du chargement des modèles d\'IA');
      } finally {
        setModelsLoading(false);
      }
    };

    fetchModels();
  }, []);

  const getStepIcon = (step) => {
    switch (step) {
      case 1: return <MusicNote />;
      case 2: return <Movie />;
      case 3: return <Image />;
      case 4: return <Animation />;
      default: return <CheckCircle />;
    }
  };

  const getStepLabel = (step) => {
    switch (step) {
      case 0: return "Projet terminé";
      case 1: return "Paroles";
      case 2: return "Scènes";
      case 3: return "Images";
      case 4: return "Animation";
      default: return "Projet terminé";
    }
  };

  const getStepStatus = (step) => {
    if (step < project?.current_step) return "completed";
    if (step === project?.current_step) return "current";
    return "pending";
  };

  const getStepColor = (status) => {
    switch (status) {
      case "completed": return "success";
      case "current": return "warning";
      case "pending": return "default";
      default: return "default";
    }
  };

  const toggleStepCollapse = (step) => {
    setCollapsedSteps(prev => ({
      ...prev,
      [step]: !prev[step]
    }));
  };


  // Function to refresh project data
  const refreshProjectData = async () => {
    try {
      const refreshedData = await clipAudioApi.getProject(id);
      setProject(refreshedData);
    } catch (error) {
      console.error('Error refreshing project data:', error);
    }
  };

  // Step generation and validation functions
  const handleStepGenerate = async (stepNumber) => {
    const stepKey = `step${stepNumber}`;
    setStepStates(prev => ({
      ...prev,
      [stepKey]: { ...prev[stepKey], isGenerating: true }
    }));
    
    try {
      // Include model selection and number of images per scene for step 3 (image generation)
      const stepData = stepNumber === 3 ? { 
        model: selectedModel || "google/gemini-2.5-flash-image",
        nb_images_per_scene: typeof nbImagesPerScene === 'number' ? nbImagesPerScene : 2
      } : {};
      const result = await clipAudioApi.executeStep(id, stepNumber, stepData);
      console.log(`Step ${stepNumber} generation successful:`, result);
      // Refresh project data to get latest state
      await refreshProjectData();
    } catch (error) {
      console.error(`Error generating step ${stepNumber}:`, error);
    } finally {
      setStepStates(prev => ({
        ...prev,
        [stepKey]: { ...prev[stepKey], isGenerating: false }
      }));
    }
  };

  // Check if at least one image is selected per scene for step 3 validation
  const hasAtLeastOneImageSelectedPerScene = () => {
    const images = project?.workflow_data?.images;
    if (!images || images.length === 0) {
      return false;
    }
    
    // Check each scene (image group)
    for (const imageGroup of images) {
      const sceneImages = imageGroup?.images || [];
      if (sceneImages.length === 0) {
        return false; // Scene has no images
      }
      
      // Check if at least one image is selected in this scene
      const hasSelectedImage = sceneImages.some(img => {
        // Handle both string and object formats
        if (typeof img === 'string') {
          return false; // String format means not selected
        }
        return img.selected === true;
      });
      
      if (!hasSelectedImage) {
        return false; // This scene has no selected image
      }
    }
    
    return true; // All scenes have at least one selected image
  };

  const handleStepValidate = async (stepNumber) => {
    const stepKey = `step${stepNumber}`;
    setStepStates(prev => ({
      ...prev,
      [stepKey]: { ...prev[stepKey], isValidating: true }
    }));
    
    try {
      const result = await clipAudioApi.validateStep(id, stepNumber);
      console.log(`Step ${stepNumber} validation successful:`, result);
      // Refresh project data to get latest state
      await refreshProjectData();
    } catch (error) {
      console.error(`Error validating step ${stepNumber}:`, error);
    } finally {
      setStepStates(prev => ({
        ...prev,
        [stepKey]: { ...prev[stepKey], isValidating: false }
      }));
    }
  };

  // Handlers for editing a scene (Step 2)
  const handleEditScene = (scene, index) => {
    if (!canEditScenes) return;
    setEditingSceneIndex(index);
    setEditingSceneText(scene?.one_line || '');
  };

  const handleCancelEditScene = () => {
    setEditingSceneIndex(null);
    setEditingSceneText('');
  };

  const handleSaveScene = async (scene, index) => {
    if (!canEditScenes) return;
    if (typeof index !== 'number') return;
    const sceneNumber = scene?.number ?? index + 1;
    const sceneDescription = String(editingSceneText ?? '').trim();
    if (!sceneDescription) return;
    setIsSavingScene(true);
    try {
      // Execute step 2 with targeted scene update
      await clipAudioApi.executeStep(id, 2, {
        scene_number: sceneNumber,
        scene_description: sceneDescription
      });
      await refreshProjectData();
      setEditingSceneIndex(null);
      setEditingSceneText('');
    } catch (e) {
      console.error('Error saving scene description:', e);
    } finally {
      setIsSavingScene(false);
    }
  };

  // Handle project deletion
  const handleDeleteProject = async () => {
    setIsDeleting(true);
    
    try {
      console.log('Deleting project:', id);
      
      // Call the API to delete the project
      await clipAudioApi.deleteProject(id);
      
      console.log('Project deleted successfully');
      
      // Navigate back to projects list
      navigate('/audio');
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Erreur lors de la suppression du projet. Veuillez réessayer.');
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#F5F7FA' }}>
        <Navigation />
        <Container maxWidth="xl" sx={{ py: 3 }}>
        <Card sx={{ borderRadius: 4, bgcolor: '#FFFFFF', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress size={60} sx={{ mb: 2, color: '#3498DB' }} />
            <Typography variant="h4" sx={{ mb: 2, color: '#2C3E50', fontWeight: 'bold' }}>
              Chargement du projet...
            </Typography>
            <Typography variant="h6" sx={{ color: '#7F8C8D' }}>
              Veuillez patienter pendant que nous récupérons les détails du projet.
            </Typography>
          </CardContent>
        </Card>
      </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', background: '#F5F7FA' }}>
        <Navigation />
        <Container maxWidth="xl" sx={{ py: 3 }}>
        <Card sx={{ borderRadius: 4, bgcolor: '#FFFFFF', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                Erreur lors du chargement du projet
              </Typography>
              <Typography variant="body1">
                {error}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: '#666' }}>
                Vérifiez que le serveur API est démarré
              </Typography>
            </Alert>
            <Button
              variant="contained"
              onClick={() => navigate('/audio')}
              sx={{
                bgcolor: '#3498DB',
                '&:hover': { bgcolor: '#2980B9' }
              }}
            >
              Retour aux projets
            </Button>
          </CardContent>
        </Card>
      </Container>
      </div>
    );
  }

  if (!project) {
    return (
      <div style={{ minHeight: '100vh', background: '#F5F7FA' }}>
        <Navigation />
        <Container maxWidth="xl" sx={{ py: 3 }}>
        <Card sx={{ borderRadius: 4, bgcolor: '#FFFFFF', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ mb: 2, color: '#2C3E50', fontWeight: 'bold' }}>
              Projet non trouvé
            </Typography>
            <Typography variant="h6" sx={{ color: '#7F8C8D', mb: 3 }}>
              Le projet demandé n'existe pas ou a été supprimé.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/audio')}
              sx={{
                bgcolor: '#3498DB',
                '&:hover': { bgcolor: '#2980B9' }
              }}
            >
              Retour aux projets
            </Button>
          </CardContent>
        </Card>
      </Container>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F5F7FA' }}>
      <Navigation />
      <Container maxWidth="xl" sx={{ py: 3 }}>
      <Card sx={{ 
        borderRadius: 4, 
        bgcolor: '#FFFFFF',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
      }}>
        <CardContent sx={{ p: 4 }}>
          {/* Header */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Button
                onClick={() => navigate('/audio')}
                startIcon={<ArrowBack />}
                variant="outlined"
                sx={{ 
                  borderColor: '#FF9800',
                  color: '#E65100',
                  fontWeight: 'bold',
                  '&:hover': {
                    borderColor: '#F57C00',
                    backgroundColor: '#FFF3E0'
                  }
                }}
              >
                Retour aux projets
              </Button>
              
              <Button
                onClick={() => setDeleteDialogOpen(true)}
                startIcon={<DeleteIcon />}
                variant="outlined"
                sx={{ 
                  borderColor: '#E53935',
                  color: '#C62828',
                  fontWeight: 'bold',
                  '&:hover': {
                    borderColor: '#C62828',
                    backgroundColor: '#FFEBEE'
                  }
                }}
              >
                Suppression
              </Button>
            </Box>
            
            <Typography variant="h3" component="h2" sx={{ 
              fontWeight: 'bold', 
              color: '#E65100',
              mb: 1
            }}>
              📋 {project.name}
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
              <Chip
                icon={getStepIcon(project.current_step)}
                label={`Étape ${project.current_step}: ${getStepLabel(project.current_step)}`}
                color="warning"
                variant="filled"
                size="large"
                sx={{ fontWeight: 'bold' }}
              />
              <Chip
                icon={<Schedule />}
                label={`Créé le ${project.created_at}`}
                variant="outlined"
                size="medium"
                sx={{ borderColor: '#FF9800', color: '#E65100' }}
              />
              <Chip
                icon={<Schedule />}
                label={`Modifié le ${project.updated_at}`}
                variant="outlined"
                size="medium"
                sx={{ borderColor: '#FF9800', color: '#E65100' }}
              />
              <Chip
                label={`Status: ${project.status}`}
                color={project.status === 'completed' ? 'success' : project.status === 'failed' ? 'error' : 'warning'}
                variant="outlined"
                size="medium"
              />
            </Box>
          </Box>

                     {/* Step 1: Theme */}
           <Card sx={{ mb: 3, borderRadius: 3, bgcolor: '#F8F9FA' }}>
             <CardContent sx={{ p: 3 }}>
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                             <Chip
              icon={getStepIcon(1)}
              label="Étape 1: Paroles"
              color={getStepColor(getStepStatus(1))}
              variant="filled"
              sx={{ fontWeight: 'bold' }}
            />
                 {getStepStatus(1) === "completed" && (
                   <CheckCircle color="success" />
                 )}
                 <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                   <Button
                     variant="contained"
                     onClick={() => handleStepGenerate(1)}
                     disabled={stepStates.step1.isGenerating || project?.current_step < 1}
                     startIcon={stepStates.step1.isGenerating ? <CircularProgress size={16} /> : <MusicNote />}
                     size="small"
                     sx={{
                       background: 'linear-gradient(135deg, #3498DB 0%, #2980B9 100%)',
                       borderRadius: '20px',
                       px: 2,
                       py: 0.5,
                       fontSize: '0.8rem',
                       fontWeight: 'bold',
                       minWidth: '100px',
                       '&:hover': {
                         background: 'linear-gradient(135deg, #2980B9 0%, #1F618D 100%)',
                         transform: 'translateY(-1px)'
                       },
                       '&:disabled': {
                         background: '#BDBDBD',
                         transform: 'none'
                       }
                     }}
                   >
                     {stepStates.step1.isGenerating ? 'Génération...' : 'Générer'}
                   </Button>
                   
                   <Button
                     variant="contained"
                     onClick={() => handleStepValidate(1)}
                     disabled={stepStates.step1.isValidating || project?.current_step < 1}
                     startIcon={stepStates.step1.isValidating ? <CircularProgress size={16} /> : <CheckCircle />}
                     size="small"
                     sx={{
                       background: 'linear-gradient(135deg, #27AE60 0%, #2ECC71 100%)',
                       borderRadius: '20px',
                       px: 2,
                       py: 0.5,
                       fontSize: '0.8rem',
                       fontWeight: 'bold',
                       minWidth: '100px',
                       '&:hover': {
                         background: 'linear-gradient(135deg, #229954 0%, #27AE60 100%)',
                         transform: 'translateY(-1px)'
                       },
                       '&:disabled': {
                         background: '#BDBDBD',
                         transform: 'none'
                       }
                     }}
                   >
                     {stepStates.step1.isValidating ? 'Validation...' : 'Valider'}
                   </Button>
                   
                   <Button
                     onClick={() => toggleStepCollapse('step1')}
                     startIcon={collapsedSteps.step1 ? <ExpandMore /> : <ExpandLess />}
                     variant="outlined"
                     size="small"
                     sx={{
                       borderColor: '#3498DB',
                       color: '#3498DB',
                       '&:hover': {
                         borderColor: '#2980B9',
                         backgroundColor: '#E8F4FD'
                       }
                     }}
                   >
                     {collapsedSteps.step1 ? 'Afficher' : 'Masquer'}
                   </Button>
                 </Box>
               </Box>
               
               {!collapsedSteps.step1 && (
                 <>
                   {project.workflow_data?.lyric?.lyric ? (
                     <Box>
                       <Typography variant="h6" sx={{ mb: 2, color: '#2C3E50', fontWeight: 'bold' }}>
                         🎼 Chanson générée
                       </Typography>
                       <Box sx={{ 
                         bgcolor: '#E8F4FD', 
                         borderRadius: 2, 
                         p: 2.5,
                         border: '2px solid #3498DB'
                       }}>
                         <Typography variant="h6" sx={{ mb: 1, color: '#2C3E50', fontWeight: 'bold' }}>
                           {project.workflow_data?.lyric?.theme}
                         </Typography>
                         <Typography
                           component="pre"
                           sx={{
                             whiteSpace: 'pre-wrap',
                             fontFamily: 'inherit',
                             fontSize: '0.95rem',
                             lineHeight: 1.6,
                             color: '#7F8C8D',
                             margin: 0
                           }}
                         >
                           {project.workflow_data?.lyric?.lyric}
                         </Typography>
                       </Box>
                     </Box>
                   ) : (
                     <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                       Aucune chanson générée pour le moment
                     </Typography>
                   )}
                 </>
               )}
             </CardContent>
           </Card>

          {/* Step 2: Scenes */}
          <Card sx={{ mb: 3, borderRadius: 3, bgcolor: '#F8F9FA' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Chip
                  icon={getStepIcon(2)}
                  label="Étape 2: Scènes"
                  color={getStepColor(getStepStatus(2))}
                  variant="filled"
                  sx={{ fontWeight: 'bold' }}
                />
                {getStepStatus(2) === "completed" && (
                  <CheckCircle color="success" />
                )}
                <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                  <Button
                    variant="contained"
                    onClick={() => handleStepGenerate(2)}
                    disabled={stepStates.step2.isGenerating || project?.current_step < 2}
                    startIcon={stepStates.step2.isGenerating ? <CircularProgress size={16} /> : <Movie />}
                    size="small"
                    sx={{
                      background: 'linear-gradient(135deg, #3498DB 0%, #2980B9 100%)',
                      borderRadius: '20px',
                      px: 2,
                      py: 0.5,
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      minWidth: '100px',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #2980B9 0%, #1F618D 100%)',
                        transform: 'translateY(-1px)'
                      },
                      '&:disabled': {
                        background: '#BDBDBD',
                        transform: 'none'
                      }
                    }}
                  >
                    {stepStates.step2.isGenerating ? 'Génération...' : 'Générer'}
                  </Button>
                  
                  <Button
                    variant="contained"
                    onClick={() => handleStepValidate(2)}
                    disabled={stepStates.step2.isValidating || project?.current_step < 2}
                    startIcon={stepStates.step2.isValidating ? <CircularProgress size={16} /> : <CheckCircle />}
                    size="small"
                    sx={{
                      background: 'linear-gradient(135deg, #27AE60 0%, #2ECC71 100%)',
                      borderRadius: '20px',
                      px: 2,
                      py: 0.5,
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      minWidth: '100px',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #229954 0%, #27AE60 100%)',
                        transform: 'translateY(-1px)'
                      },
                      '&:disabled': {
                        background: '#BDBDBD',
                        transform: 'none'
                      }
                    }}
                  >
                    {stepStates.step2.isValidating ? 'Validation...' : 'Valider'}
                  </Button>
                  
                  <Button
                    onClick={() => toggleStepCollapse('step2')}
                    startIcon={collapsedSteps.step2 ? <ExpandMore /> : <ExpandLess />}
                    variant="outlined"
                    size="small"
                    sx={{
                      borderColor: '#3498DB',
                      color: '#3498DB',
                      '&:hover': {
                        borderColor: '#2980B9',
                        backgroundColor: '#E8F4FD'
                      }
                    }}
                  >
                    {collapsedSteps.step2 ? 'Afficher' : 'Masquer'}
                  </Button>
                </Box>
              </Box>
              
              {!collapsedSteps.step2 && (
                <>
                  {project.workflow_data?.scenes?.scenes ? (
                    <Box>
                      <Typography variant="h6" sx={{ mb: 2, color: '#2C3E50', fontWeight: 'bold' }}>
                        🎬 Scènes générées ({project.workflow_data.scenes.scenes.length})
                      </Typography>
                      
                      {/* Table for lyric_line and scene description */}
                      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
                        <Table>
                          <TableHead>
                            <TableRow sx={{ bgcolor: '#F8F9FA' }}>
                              <TableCell sx={{ fontWeight: 'bold', color: '#2C3E50', fontSize: '1rem' }}>
                                🎵 Ligne de paroles
                              </TableCell>
                              <TableCell sx={{ fontWeight: 'bold', color: '#2C3E50', fontSize: '1rem' }}>
                                📝 Description de la scène
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {project.workflow_data.scenes.scenes.map((scene, index) => (
                              <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: '#FAFAFA' } }}>
                                <TableCell sx={{ 
                                  fontWeight: 'bold', 
                                  color: '#E65100',
                                  fontSize: '0.95rem',
                                  verticalAlign: 'top',
                                  width: '40%'
                                }}>
                                  {scene.lyric_line || 'N/A'}
                                </TableCell>
                                <TableCell sx={{ 
                                  color: '#2C3E50',
                                  fontSize: '0.95rem',
                                  lineHeight: 1.6,
                                  verticalAlign: 'top'
                                }}>
                                  {editingSceneIndex === index && canEditScenes ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                      <TextField
                                        fullWidth
                                        size="small"
                                        value={editingSceneText}
                                        onChange={(e) => setEditingSceneText(e.target.value)}
                                        multiline
                                        minRows={2}
                                      />
                                      <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button
                                          variant="contained"
                                          size="small"
                                          onClick={() => handleSaveScene(scene, index)}
                                          disabled={isSavingScene}
                                        >
                                          {isSavingScene ? 'Enregistrement...' : 'Sauvegarder'}
                                        </Button>
                                        <Button
                                          variant="outlined"
                                          size="small"
                                          onClick={handleCancelEditScene}
                                          disabled={isSavingScene}
                                        >
                                          Annuler
                                        </Button>
                                      </Box>
                                    </Box>
                                  ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                                      <Box sx={{ flex: 1 }}>
                                        {scene.one_line}
                                      </Box>
                                      {canEditScenes && (
                                        <Button
                                          variant="outlined"
                                          size="small"
                                          onClick={() => handleEditScene(scene, index)}
                                        >
                                          Éditer
                                        </Button>
                                      )}
                                    </Box>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  ) : (
                    <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      Aucune scène générée pour le moment
                    </Typography>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Step 3: Images */}
          <Card sx={{ mb: 3, borderRadius: 3, bgcolor: '#F8F9FA' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Chip
                  icon={getStepIcon(3)}
                  label="Étape 3: Images"
                  color={getStepColor(getStepStatus(3))}
                  variant="filled"
                  sx={{ fontWeight: 'bold' }}
                />
                {getStepStatus(3) === "completed" && (
                  <CheckCircle color="success" />
                )}
                <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                  <Button
                    variant="contained"
                    onClick={() => handleStepGenerate(3)}
                    disabled={stepStates.step3.isGenerating || project?.current_step < 3}
                    startIcon={stepStates.step3.isGenerating ? <CircularProgress size={16} /> : <Image />}
                    size="small"
                    sx={{
                      background: 'linear-gradient(135deg, #3498DB 0%, #2980B9 100%)',
                      borderRadius: '20px',
                      px: 2,
                      py: 0.5,
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      minWidth: '100px',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #2980B9 0%, #1F618D 100%)',
                        transform: 'translateY(-1px)'
                      },
                      '&:disabled': {
                        background: '#BDBDBD',
                        transform: 'none'
                      }
                    }}
                  >
                    {stepStates.step3.isGenerating ? 'Génération...' : 'Générer'}
                  </Button>
                  
                  <Button
                    variant="contained"
                    onClick={() => handleStepValidate(3)}
                    disabled={stepStates.step3.isValidating || project?.current_step < 3 || !hasAtLeastOneImageSelectedPerScene()}
                    startIcon={stepStates.step3.isValidating ? <CircularProgress size={16} /> : <CheckCircle />}
                    size="small"
                    sx={{
                      background: 'linear-gradient(135deg, #27AE60 0%, #2ECC71 100%)',
                      borderRadius: '20px',
                      px: 2,
                      py: 0.5,
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      minWidth: '100px',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #229954 0%, #27AE60 100%)',
                        transform: 'translateY(-1px)'
                      },
                      '&:disabled': {
                        background: '#BDBDBD',
                        transform: 'none'
                      }
                    }}
                  >
                    {stepStates.step3.isValidating ? 'Validation...' : 'Valider'}
                  </Button>
                  
                  <Button
                    onClick={() => toggleStepCollapse('step3')}
                    startIcon={collapsedSteps.step3 ? <ExpandMore /> : <ExpandLess />}
                    variant="outlined"
                    size="small"
                    sx={{
                      borderColor: '#3498DB',
                      color: '#3498DB',
                      '&:hover': {
                        borderColor: '#2980B9',
                        backgroundColor: '#E8F4FD'
                      }
                    }}
                  >
                    {collapsedSteps.step3 ? 'Afficher' : 'Masquer'}
                  </Button>
                </Box>
              </Box>
              
              {/* Model Selection and Number of Images per Scene for Step 3 */}
              {!collapsedSteps.step3 && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: '#F8F9FA', borderRadius: 1, border: '1px solid #E0E0E0' }}>
                  {modelsLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={14} />
                      <Typography variant="caption" color="text.secondary">
                        Chargement des modèles...
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: '80px', fontSize: '0.75rem' }}>
                        🤖 Modèle pour la génération des images:
                      </Typography>
                      <Box sx={{ minWidth: '150px', maxWidth: '200px' }}>
                        <Select
                          value={selectedModel}
                          onChange={(e) => setSelectedModel(e.target.value)}
                          size="small"
                          sx={{
                            fontSize: '0.8rem',
                            height: '28px',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#3498DB',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#2980B9',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#2980B9',
                            },
                            '& .MuiSelect-select': {
                              padding: '4px 8px',
                              fontSize: '0.8rem',
                            },
                          }}
                        >
                          {imageGenerationModels.map((model) => (
                            <MenuItem key={model.id} value={model.id} sx={{ fontSize: '0.8rem', py: 0.5 }}>
                              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>
                                {model.name}
                              </Typography>
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                      
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: '120px', fontSize: '0.75rem', ml: 2 }}>
                        🖼️ Nombre de variations par scène:
                      </Typography>
                      <Box sx={{ minWidth: '80px', maxWidth: '100px' }}>
                        <Select
                          value={nbImagesPerScene}
                          onChange={(e) => setNbImagesPerScene(e.target.value)}
                          size="small"
                          sx={{
                            fontSize: '0.8rem',
                            height: '28px',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#3498DB',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#2980B9',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#2980B9',
                            },
                            '& .MuiSelect-select': {
                              padding: '4px 8px',
                              fontSize: '0.8rem',
                            },
                          }}
                        >
                          {[1, 2, 3, 4, 5].map((num) => (
                            <MenuItem key={num} value={num} sx={{ fontSize: '0.8rem', py: 0.5 }}>
                              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>
                                {num}
                              </Typography>
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
              
              {!collapsedSteps.step3 && (
                <ImagesTable 
                  images={project.workflow_data?.images}
                  projectId={id}
                  onProjectUpdate={refreshProjectData}
                  selectedModel={selectedModel}
                  modelsLoading={modelsLoading}
                  nbImagesPerScene={nbImagesPerScene}
                />
              )}
            </CardContent>
          </Card>

          {/* Step 4: Animation */}
          <Card sx={{ borderRadius: 3, bgcolor: '#F8F9FA' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Chip
                  icon={getStepIcon(4)}
                  label="Étape 4: Animation"
                  color={getStepColor(getStepStatus(4))}
                  variant="filled"
                  sx={{ fontWeight: 'bold' }}
                />
                {getStepStatus(4) === "completed" && (
                  <CheckCircle color="success" />
                )}
                <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                  <Button
                    variant="contained"
                    onClick={() => handleStepGenerate(4)}
                    disabled={stepStates.step4.isGenerating || project?.current_step < 4}
                    startIcon={stepStates.step4.isGenerating ? <CircularProgress size={16} /> : <Animation />}
                    size="small"
                    sx={{
                      background: 'linear-gradient(135deg, #3498DB 0%, #2980B9 100%)',
                      borderRadius: '20px',
                      px: 2,
                      py: 0.5,
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      minWidth: '100px',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #2980B9 0%, #1F618D 100%)',
                        transform: 'translateY(-1px)'
                      },
                      '&:disabled': {
                        background: '#BDBDBD',
                        transform: 'none'
                      }
                    }}
                  >
                    {stepStates.step4.isGenerating ? 'Génération...' : 'Générer'}
                  </Button>
                  
                  <Button
                    variant="contained"
                    onClick={() => handleStepValidate(4)}
                    disabled={stepStates.step4.isValidating || project?.current_step < 4}
                    startIcon={stepStates.step4.isValidating ? <CircularProgress size={16} /> : <CheckCircle />}
                    size="small"
                    sx={{
                      background: 'linear-gradient(135deg, #27AE60 0%, #2ECC71 100%)',
                      borderRadius: '20px',
                      px: 2,
                      py: 0.5,
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      minWidth: '100px',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #229954 0%, #27AE60 100%)',
                        transform: 'translateY(-1px)'
                      },
                      '&:disabled': {
                        background: '#BDBDBD',
                        transform: 'none'
                      }
                    }}
                  >
                    {stepStates.step4.isValidating ? 'Validation...' : 'Valider'}
                  </Button>
                  
                  <Button
                    onClick={() => toggleStepCollapse('step4')}
                    startIcon={collapsedSteps.step4 ? <ExpandMore /> : <ExpandLess />}
                    variant="outlined"
                    size="small"
                    sx={{
                      borderColor: '#3498DB',
                      color: '#3498DB',
                      '&:hover': {
                        borderColor: '#2980B9',
                        backgroundColor: '#E8F4FD'
                      }
                    }}
                  >
                    {collapsedSteps.step4 ? 'Afficher' : 'Masquer'}
                  </Button>
                </Box>
              </Box>
              
              {!collapsedSteps.step4 && (
                <ImagesAnglesDisplay 
                  imagesAngles={project.workflow_data?.imagesAngles}
                  projectId={id}
                  onProjectUpdate={refreshProjectData}
                />
              )}
            </CardContent>
          </Card>
        </CardContent>
      </Card>
    </Container>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            '& .MuiDialog-paper': {
              borderRadius: '20px'
            }
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          pb: 1,
          background: 'linear-gradient(135deg, #E53935 0%, #C62828 100%)',
          color: 'white',
          borderRadius: '20px 20px 0 0'
        }}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <WarningIcon />
            Confirmation de suppression
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#2C3E50' }}>
            ⚠️ Attention
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: '#6b7280' }}>
            Êtes-vous sûr de vouloir supprimer ce projet ?
          </Typography>
          <Typography variant="body2" sx={{ color: '#E53935', fontWeight: 'bold' }}>
            {project?.name}
          </Typography>
          <Typography variant="body2" sx={{ mt: 2, color: '#9ca3af' }}>
            Cette action est irréversible. Toutes les données de ce projet seront définitivement supprimées.
          </Typography>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            disabled={isDeleting}
            sx={{
              borderRadius: '25px',
              px: 3,
              py: 1,
              borderColor: '#95A5A6',
              color: '#7F8C8D',
              '&:hover': {
                borderColor: '#7F8C8D',
                backgroundColor: '#F8F9FA'
              }
            }}
          >
            Annuler
          </Button>
          
          <Button
            onClick={handleDeleteProject}
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
            sx={{
              background: 'linear-gradient(135deg, #E53935 0%, #C62828 100%)',
              borderRadius: '25px',
              px: 3,
              py: 1,
              fontWeight: 'bold',
              '&:hover': {
                background: 'linear-gradient(135deg, #C62828 0%, #B71C1C 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(229, 57, 53, 0.4)'
              },
              '&:disabled': {
                background: '#BDBDBD',
                transform: 'none',
                boxShadow: 'none'
              }
            }}
          >
            {isDeleting ? 'Suppression...' : 'Confirmer la suppression'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default ProjectDetail;
